import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  LayoutDashboard, Search, Star, FileText, Settings,
  Filter, ArrowUpRight, ExternalLink, Bell, Mail,
  MessageCircle, Clock, Calendar, MapPin, Building2,
  Tag, ChevronDown, X, Plus, Trash2, Eye, Send,
  Download, RefreshCw, AlertTriangle, CheckCircle,
  Info, Zap, Target, Award, TrendingUp, BarChart3,
  Grid3X3, List, FolderOpen, Bookmark, Menu, XCircle,
  ChevronLeft, ChevronRight, GripVertical, ChevronsUpDown,
} from 'lucide-react';

const SCORE_COLOR = (s) => s >= 70 ? '#22c55e' : s >= 50 ? '#eab308' : '#ef4444';
const SCORE_BG = (s) => s >= 70 ? 'bg-green-50 border-green-200' : s >= 50 ? 'bg-yellow-50 border-yellow-200' : 'bg-red-50 border-red-200';
const SCORE_TEXT = (s) => s >= 70 ? 'text-green-700' : s >= 50 ? 'text-yellow-700' : 'text-red-700';
const CHANCE_BADGE = { Alta: 'bg-green-100 text-green-800', Média: 'bg-yellow-100 text-yellow-800', Baixa: 'bg-red-100 text-red-800' };

const fmtCurrency = (v) => {
  if (!v) return 'Não informado';
  return `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
};
const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('pt-BR'); } catch { return d; }
};

const DEFAULT_CONFIG = {
  keywords: ['logística', 'transporte', 'armazenagem', 'tecnologia', 'equipamentos médicos', 'RMA'],
  cnae_codes: [],
  min_score: 50,
  alert_email: true,
  alert_whatsapp: false,
  alert_email_addresses: [],
  alert_whatsapp_numbers: [],
  auto_search_enabled: false,
  auto_search_interval_hours: 24,
  days_lookback: 7,
  uf: 'SP',
  fontes: ['pncp', 'comprasnet', 'queridodiario'],
  modalidades: [6, 7, 4, 5, 8, 9],
  valor_min: null,
  valor_max: null,
  data_inicio: null,
  data_fim: null,
  municipio_ibge: null,
};

const MODALIDADES_PNCP = [
  { id: 6, label: 'Pregão Eletrônico' },
  { id: 7, label: 'Pregão Presencial' },
  { id: 4, label: 'Concorrência Eletrônica' },
  { id: 5, label: 'Concorrência Presencial' },
  { id: 8, label: 'Dispensa de Licitação' },
  { id: 9, label: 'Inexigibilidade' },
  { id: 1, label: 'Leilão Eletrônico' },
  { id: 13, label: 'Leilão Presencial' },
  { id: 3, label: 'Concurso' },
  { id: 2, label: 'Diálogo Competitivo' },
  { id: 10, label: 'Manifestação de Interesse' },
  { id: 11, label: 'Pré-qualificação' },
  { id: 12, label: 'Credenciamento' },
];

const FONTE_META = {
  pncp: { label: 'PNCP', desc: 'Portal Nacional de Contratações Públicas (federal + estados)', color: 'bg-blue-100 text-blue-700' },
  comprasnet: { label: 'ComprasNet', desc: 'Compras.gov.br — licitações desde 1976 (federal)', color: 'bg-emerald-100 text-emerald-700' },
  queridodiario: { label: 'Diários Oficiais', desc: 'Querido Diário — +700 municípios e estados', color: 'bg-orange-100 text-orange-700' },
};

const TAB_DEFS = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'resultados', label: 'Resultados', icon: List },
  { id: 'salvas', label: 'Salvas', icon: Bookmark },
  { id: 'edital', label: 'Analisar Edital', icon: FileText },
  { id: 'config', label: 'Configurações', icon: Settings },
];

export default function NexoBid({ api }) {
  const [tab, setTab] = useState('dashboard');
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [results, setResults] = useState([]);
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [selectedLic, setSelectedLic] = useState(null);
  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfAnalysis, setPdfAnalysis] = useState(null);
  const [analyzingPdf, setAnalyzingPdf] = useState(false);
  const [newKw, setNewKw] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [sendingAlert, setSendingAlert] = useState(null);
  const [minScoreFilter, setMinScoreFilter] = useState(0);
  const [maxScoreFilter, setMaxScoreFilter] = useState(100);
  const [showFilters, setShowFilters] = useState(false);
  const [municipios, setMunicipios] = useState([]);
  const [municipioSearch, setMunicipioSearch] = useState('');

  useEffect(() => {
    fetchConfig();
    fetchSaved();
  }, []);

  useEffect(() => {
    if (!config.uf) return;
    setMunicipios([]);
    setMunicipioSearch('');
    fetch(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${config.uf}/municipios?orderBy=nome`)
      .then(r => r.json())
      .then(data => setMunicipios(data.map(m => ({ id: m.id, nome: m.nome }))))
      .catch(() => {});
  }, [config.uf]);

  const fetchConfig = async () => {
    try {
      const r = await api.get('/nexobid/config');
      setConfig({ ...DEFAULT_CONFIG, ...r.data });
    } catch {}
  };
  const fetchSaved = async () => {
    try { const r = await api.get('/nexobid/saved'); setSaved(r.data || []); } catch {}
  };

  const handleSearch = async () => {
    setLoading(true);
    setResults([]);
    const fonteLabels = (config.fontes || DEFAULT_CONFIG.fontes).map(f => FONTE_META[f]?.label).filter(Boolean).join(', ');
    toast.info(`Buscando licitações em: ${fonteLabels} — analisando com IA…`);
    try {
      const r = await api.post('/nexobid/search');
      setResults(r.data.results || []);
      if (r.data.results?.length === 0) {
        toast.warning(r.data.message || 'Nenhuma licitação encontrada');
      } else {
        const counts = r.data.fonte_counts || {};
        const detail = Object.entries(counts).map(([k, v]) => `${FONTE_META[k]?.label || k}: ${v}`).join(' · ');
        toast.success(`${r.data.total} licitações analisadas! ${detail ? `(${detail})` : ''}`, { duration: 6000 });
        setTab('resultados');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao buscar licitações');
    } finally { setLoading(false); }
  };

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    try { await api.post('/nexobid/config', config); toast.success('Configurações salvas!'); }
    catch { toast.error('Erro ao salvar configurações'); }
    finally { setSavingConfig(false); }
  };

  const handleSave = async (lic) => {
    try {
      await api.post('/nexobid/saved', lic);
      toast.success('Licitação salva!');
      fetchSaved();
    } catch { toast.error('Erro ao salvar'); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/nexobid/saved/${id}`);
      toast.success('Removida das salvas');
      fetchSaved();
    } catch { toast.error('Erro ao remover'); }
  };

  const handleAlert = async (lic) => {
    setSendingAlert(lic.numero_controle_pncp);
    try {
      const r = await api.post('/nexobid/send-alert', {
        licitacao: lic,
        whatsapp_numbers: config.alert_whatsapp_numbers || [],
        email_addresses: config.alert_email_addresses || [],
      });
      const emailSent = r.data?.email?.sent || 0;
      const wpSent = r.data?.whatsapp?.sent || 0;
      if (emailSent > 0 || wpSent > 0) {
        toast.success(`Alerta enviado: ${emailSent} e-mail(s), ${wpSent} WhatsApp(s)`);
      } else {
        const emailErrs = (r.data?.email?.results || []).filter(e => !e.ok).map(e => e.error || 'falhou');
        const wpErr = r.data?.whatsapp?.error;
        const parts = [];
        if (emailErrs.length) parts.push(`E-mail: ${emailErrs[0]}`);
        if (wpErr) parts.push(`WhatsApp: ${wpErr}`);
        const detail = parts.length ? parts.join(' | ') : 'Verifique o SMTP e as configurações de alerta.';
        toast.warning(`Nenhum alerta enviado. ${detail}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao enviar alerta');
    } finally { setSendingAlert(null); }
  };

  const handleAnalyzePdf = async () => {
    if (!pdfUrl.trim()) return;
    setAnalyzingPdf(true);
    setPdfAnalysis(null);
    try {
      const r = await api.post('/nexobid/analyze-pdf', { url: pdfUrl });
      setPdfAnalysis(r.data.analysis);
      toast.success('Edital analisado com sucesso!');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao analisar edital');
    } finally { setAnalyzingPdf(false); }
  };

  const buildPncpUrl = (lic) => {
    if (lic.link_sistema_origem) return lic.link_sistema_origem;
    const nc = lic.numero_controle_pncp || '';
    if (!nc) return '';
    try {
      const parts = nc.split('-');
      if (parts.length >= 3) {
        const cnpj = parts[0];
        const last = parts[parts.length - 1];
        const [seqStr, ano] = last.split('/');
        const seq = parseInt(seqStr, 10);
        if (cnpj && ano && !isNaN(seq)) {
          return `https://pncp.gov.br/app/editais/${cnpj}/${ano}/${seq}`;
        }
      }
    } catch {}
    return '';
  };

  const handleSendToEdital = (lic) => {
    const url = buildPncpUrl(lic);
    setPdfUrl(url);
    setPdfAnalysis(null);
    setTab('edital');
    if (!url) toast.info('Não foi possível gerar o link. Cole a URL do edital manualmente.');
    else toast.info('Link do PNCP carregado. Clique em Analisar para iniciar.');
  };

  const addKeyword = () => {
    const kw = newKw.trim().toLowerCase();
    if (kw && !config.keywords.includes(kw)) {
      setConfig(c => ({ ...c, keywords: [...c.keywords, kw] }));
      setNewKw('');
    }
  };
  const removeKeyword = (kw) => setConfig(c => ({ ...c, keywords: c.keywords.filter(k => k !== kw) }));

  const addEmail = () => {
    const em = newEmail.trim().toLowerCase();
    if (em && em.includes('@') && !(config.alert_email_addresses || []).includes(em)) {
      setConfig(c => ({ ...c, alert_email_addresses: [...(c.alert_email_addresses || []), em] }));
      setNewEmail('');
    }
  };
  const removeEmail = (em) => setConfig(c => ({ ...c, alert_email_addresses: (c.alert_email_addresses || []).filter(e => e !== em) }));

  const addPhone = () => {
    const ph = newPhone.trim().replace(/\s/g, '');
    if (ph && !(config.alert_whatsapp_numbers || []).includes(ph)) {
      setConfig(c => ({ ...c, alert_whatsapp_numbers: [...(c.alert_whatsapp_numbers || []), ph] }));
      setNewPhone('');
    }
  };
  const removePhone = (ph) => setConfig(c => ({ ...c, alert_whatsapp_numbers: (c.alert_whatsapp_numbers || []).filter(p => p !== ph) }));

  const activeFilterCount = (minScoreFilter > 0 ? 1 : 0) + (maxScoreFilter < 100 ? 1 : 0);
  const filteredResults = results.filter(r => r.score_aderencia >= minScoreFilter && r.score_aderencia <= maxScoreFilter);

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600 text-white">
              <Search className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-slate-900">NexoBid</h1>
              <p className="text-sm text-slate-500">Monitor Multi-fonte — Licitações com Score de Aderência por IA</p>
            </div>
          </div>
          <button
            onClick={handleSearch}
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
          >
            {loading ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                Analisando…
              </>
            ) : (
              <>
                <Search className="h-4 w-4" />
                Buscar Licitações
              </>
            )}
          </button>
        </header>

        <nav className="flex gap-1 rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
          {TAB_DEFS.map(t => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium transition ${
                  tab === t.id
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                <Icon className="h-4 w-4" />
                {t.label}
              </button>
            );
          })}
        </nav>

        {tab === 'dashboard' && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              {[
                { label: 'Licitações Encontradas', value: results.length || '—', icon: List, color: 'text-blue-600', bg: 'bg-blue-50' },
                { label: 'Score Médio', value: results.length ? Math.round(results.reduce((a, r) => a + r.score_aderencia, 0) / results.length) + '/100' : '—', icon: Target, color: 'text-emerald-600', bg: 'bg-emerald-50' },
                { label: 'Alta Aderência (≥70)', value: results.filter(r => r.score_aderencia >= 70).length || '—', icon: Award, color: 'text-amber-600', bg: 'bg-amber-50' },
                { label: 'Salvas', value: saved.length || '—', icon: Bookmark, color: 'text-violet-600', bg: 'bg-violet-50' },
              ].map(stat => (
                <div key={stat.label} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${stat.bg}`}>
                      <stat.icon className={`h-5 w-5 ${stat.color}`} />
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{stat.label}</p>
                      <p className="mt-1 text-2xl font-bold text-slate-900">{stat.value}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {config.auto_search_enabled && (
              <div className="flex items-start gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4">
                <Bell className="mt-0.5 h-5 w-5 text-blue-600" />
                <div>
                  <p className="text-sm font-semibold text-blue-800">Busca Automática Ativada</p>
                  <p className="mt-1 text-xs text-blue-600">
                    Executa a cada {config.auto_search_interval_hours}h e envia alertas para {(config.alert_email_addresses || []).length} e-mail(s)
                    {(config.alert_whatsapp_numbers || []).length > 0 ? ` e ${config.alert_whatsapp_numbers.length} WhatsApp(s)` : ''}.
                  </p>
                </div>
              </div>
            )}

            {results.length === 0 ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-blue-50">
                  <Search className="h-8 w-8 text-blue-400" />
                </div>
                <h3 className="mt-4 text-lg font-semibold text-slate-900">Pronto para monitorar licitações</h3>
                <p className="mt-2 text-sm text-slate-500 max-w-md mx-auto">
                  Clique em <strong>Buscar Licitações</strong> para consultar o PNCP com suas palavras-chave e receber scores de aderência por IA.
                </p>
                <div className="mt-6 grid grid-cols-1 gap-4 max-w-lg mx-auto sm:grid-cols-3">
                  {[
                    { icon: Search, title: 'Busca automática', desc: 'PNCP com filtros por UF e palavras-chave', color: 'bg-blue-50 text-blue-600' },
                    { icon: Zap, title: 'Score por IA', desc: 'Groq/Llama 3.3 analisa aderência 0-100', color: 'bg-emerald-50 text-emerald-600' },
                    { icon: Bell, title: 'Alertas', desc: 'E-mail + WhatsApp para vários destinatários', color: 'bg-violet-50 text-violet-600' },
                  ].map(f => (
                    <div key={f.title} className="rounded-xl border border-slate-200 bg-white p-4 text-left">
                      <div className={`inline-flex h-8 w-8 items-center justify-center rounded-lg ${f.color}`}>
                        <f.icon className="h-4 w-4" />
                      </div>
                      <p className="mt-3 text-sm font-semibold text-slate-900">{f.title}</p>
                      <p className="mt-1 text-xs text-slate-500">{f.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div>
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-slate-900">Top Licitações por Score</h2>
                  <button onClick={() => setTab('resultados')} className="text-sm font-medium text-blue-600 hover:text-blue-800">
                    Ver todas as {results.length} →
                  </button>
                </div>
                <div className="space-y-3">
                  {results.slice(0, 5).map(lic => (
                    <LicCard key={lic.numero_controle_pncp} lic={lic}
                      onSave={() => handleSave(lic)} onAlert={() => handleAlert(lic)}
                      onDetails={() => setSelectedLic(lic)} onAnalyze={() => handleSendToEdital(lic)}
                      sendingAlert={sendingAlert === lic.numero_controle_pncp} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === 'resultados' && (
          <div className="space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              <button type="button" onClick={() => setShowFilters(!showFilters)} className="flex w-full items-center justify-between px-5 py-3 text-left hover:bg-slate-50 transition">
                <div className="flex items-center gap-3">
                  <Filter className="h-4 w-4 text-slate-400" />
                  <span className="text-sm font-semibold text-slate-900">Filtros</span>
                  {activeFilterCount > 0 && (
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-[10px] font-bold text-white">{activeFilterCount}</span>
                  )}
                </div>
                <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${showFilters ? 'rotate-180' : ''}`} />
              </button>
              {showFilters && (
                <div className="border-t border-slate-100 px-5 py-4 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-medium text-slate-500 block mb-1">Score mínimo</label>
                      <input type="range" min="0" max="100" value={minScoreFilter}
                        onChange={e => setMinScoreFilter(Number(e.target.value))} className="w-full accent-blue-600" />
                      <div className="flex justify-between text-xs text-slate-400 mt-0.5"><span>0</span><span>{minScoreFilter}</span><span>100</span></div>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-500 block mb-1">Score máximo</label>
                      <input type="range" min="0" max="100" value={maxScoreFilter}
                        onChange={e => setMaxScoreFilter(Number(e.target.value))} className="w-full accent-blue-600" />
                      <div className="flex justify-between text-xs text-slate-400 mt-0.5"><span>0</span><span>{maxScoreFilter}</span><span>100</span></div>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500 block mb-2">Fonte</label>
                    <div className="flex flex-wrap gap-1.5">
                      {Object.entries(FONTE_META).map(([key, meta]) => {
                        const active = (config.fontes || []).includes(key);
                        return (
                          <button key={key} type="button" onClick={() => {
                            const cur = config.fontes || [];
                            setConfig(c => ({
                              ...c,
                              fontes: active ? cur.filter(f => f !== key) : [...cur, key]
                            }));
                          }} className={`rounded-full px-2.5 py-1 text-xs font-medium transition border ${active ? 'border-blue-300 bg-blue-50 text-blue-800' : 'border-slate-200 bg-slate-50 text-slate-600 hover:border-slate-300'}`}>
                            {meta.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <button type="button" onClick={() => { setMinScoreFilter(0); setMaxScoreFilter(100); setConfig(c => ({ ...c, fontes: [...DEFAULT_CONFIG.fontes] })); }} className="text-xs text-red-500 hover:underline">Limpar todos os filtros</button>
                    <span className="text-xs text-slate-400">{filteredResults.length} de {results.length} resultados</span>
                  </div>
                </div>
              )}
            </div>
            {filteredResults.length === 0 ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-400">
                {results.length === 0 ? 'Execute uma busca para ver os resultados.' : 'Nenhuma licitação corresponde aos filtros aplicados.'}
              </div>
            ) : (
              <div className="space-y-3">
                {filteredResults.map(lic => (
                  <LicCard key={lic.numero_controle_pncp} lic={lic}
                    onSave={() => handleSave(lic)} onAlert={() => handleAlert(lic)}
                    onDetails={() => setSelectedLic(lic)} onAnalyze={() => handleSendToEdital(lic)}
                    sendingAlert={sendingAlert === lic.numero_controle_pncp} />
                ))}
              </div>
            )}
          </div>
        )}

        {tab === 'salvas' && (
          <div className="space-y-3">
            {saved.length === 0 ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-400">
                Nenhuma licitação salva ainda.
              </div>
            ) : saved.map(lic => (
              <LicCard key={lic.id} lic={lic} showDelete onDelete={() => handleDelete(lic.id)}
                onAlert={() => handleAlert(lic)} onDetails={() => setSelectedLic(lic)}
                onAnalyze={() => handleSendToEdital(lic)}
                sendingAlert={sendingAlert === lic.numero_controle_pncp} />
            ))}
          </div>
        )}

        {tab === 'edital' && (
          <div className="space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-900 mb-1">Analisar Edital por IA</h2>
              <p className="text-sm text-slate-500 mb-4">
                Cole a URL do PDF do edital. A IA irá extrair automaticamente: objeto, valor, garantias, certidões, prazos e critério de julgamento.
              </p>
              <div className="flex gap-3">
                <input className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://pncp.gov.br/...edital.pdf"
                  value={pdfUrl} onChange={e => setPdfUrl(e.target.value)} />
                <button onClick={handleAnalyzePdf} disabled={analyzingPdf || !pdfUrl.trim()}
                  className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300">
                  {analyzingPdf ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      Analisando…
                    </>
                  ) : (
                    <>
                      <Zap className="h-4 w-4" />
                      Analisar
                    </>
                  )}
                </button>
              </div>
            </div>
            {pdfAnalysis && (
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm space-y-4">
                <h3 className="text-lg font-semibold text-slate-900">Resultado da Análise</h3>
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                  <p className="text-sm text-blue-800"><strong>Resumo:</strong> {pdfAnalysis.resumo}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: 'Objeto', value: pdfAnalysis.objeto },
                    { label: 'Valor Estimado', value: pdfAnalysis.valor_estimado },
                    { label: 'Órgão', value: pdfAnalysis.orgao },
                    { label: 'Prazo de Entrega', value: pdfAnalysis.prazo_entrega },
                    { label: 'Critério de Julgamento', value: pdfAnalysis.criterio_julgamento },
                    { label: 'Prazo da Proposta', value: pdfAnalysis.prazo_proposta },
                    { label: 'Garantias Exigidas', value: pdfAnalysis.garantias_exigidas },
                    { label: 'Habilitação Técnica', value: pdfAnalysis.habilitacao_tecnica },
                  ].filter(f => f.value).map(f => (
                    <div key={f.label} className="rounded-lg bg-slate-50 p-3">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">{f.label}</p>
                      <p className="text-sm text-slate-800">{f.value}</p>
                    </div>
                  ))}
                </div>
                {pdfAnalysis.certidoes_exigidas?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Certidões Exigidas</p>
                    <div className="flex flex-wrap gap-2">
                      {pdfAnalysis.certidoes_exigidas.map(c => (
                        <span key={c} className="rounded-full bg-orange-100 px-2 py-1 text-xs font-medium text-orange-800">{c}</span>
                      ))}
                    </div>
                  </div>
                )}
                {pdfAnalysis.pontos_atencao?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Pontos de Atenção</p>
                    <ul className="space-y-1">
                      {pdfAnalysis.pontos_atencao.map(p => (
                        <li key={p} className="text-sm text-orange-700 rounded bg-orange-50 px-3 py-1.5">• {p}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {tab === 'config' && (
          <div className="space-y-4">
            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-900 mb-1">Fontes de Dados</h2>
              <p className="text-xs text-slate-500 mb-4">Todas gratuitas — selecione as que deseja consultar em cada busca.</p>
              <div className="space-y-3">
                {Object.entries(FONTE_META).map(([key, meta]) => {
                  const active = (config.fontes || []).includes(key);
                  return (
                    <label key={key} className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition ${active ? 'border-blue-300 bg-blue-50' : 'border-slate-200 bg-slate-50'}`}>
                      <input type="checkbox" checked={active} className="mt-0.5 h-4 w-4 accent-blue-600"
                        onChange={e => {
                          const current = config.fontes || [];
                          setConfig(c => ({
                            ...c,
                            fontes: e.target.checked ? [...current, key] : current.filter(f => f !== key)
                          }));
                        }} />
                      <div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${meta.color}`}>{meta.label}</span>
                          <span className="text-xs font-medium text-emerald-600">Gratuita</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">{meta.desc}</p>
                      </div>
                    </label>
                  );
                })}
              </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Filtros de Busca</h2>

              <div className="grid grid-cols-3 gap-4 mb-5">
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">UF</label>
                  <select className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" value={config.uf}
                    onChange={e => setConfig(c => ({ ...c, uf: e.target.value, municipio_ibge: null }))}>
                    {['SP','RJ','MG','ES','BA','RS','PR','SC','GO','DF','PE','CE','PA','AM','MT','MS','RO','AC','RR','AP','TO','MA','PI','RN','PB','AL','SE'].map(uf => (
                      <option key={uf} value={uf}>{uf}</option>
                    ))}
                  </select>
                </div>
                <div className="relative">
                  <label className="text-sm font-medium text-slate-700 block mb-1">Município <span className="font-normal text-slate-400">(opcional — PNCP)</span></label>
                  <input type="text" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={municipios.length ? `${municipios.length} municípios em ${config.uf}` : 'Carregando…'}
                    value={config.municipio_ibge ? (municipios.find(m => m.id === config.municipio_ibge)?.nome || `IBGE: ${config.municipio_ibge}`) : municipioSearch}
                    onChange={e => {
                      setMunicipioSearch(e.target.value);
                      if (!e.target.value) setConfig(c => ({ ...c, municipio_ibge: null }));
                    }} />
                  {municipioSearch && !config.municipio_ibge && (() => {
                    const filtered = municipios.filter(m => m.nome.toLowerCase().includes(municipioSearch.toLowerCase())).slice(0, 8);
                    if (!filtered.length) return null;
                    return (
                      <div className="absolute z-20 top-full left-0 right-0 mt-0.5 rounded-lg border border-slate-200 bg-white shadow-lg max-h-48 overflow-y-auto">
                        {filtered.map(m => (
                          <button key={m.id} onClick={() => { setConfig(c => ({ ...c, municipio_ibge: m.id })); setMunicipioSearch(''); }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b last:border-b-0">{m.nome}</button>
                        ))}
                      </div>
                    );
                  })()}
                  {config.municipio_ibge && (
                    <button onClick={() => { setConfig(c => ({ ...c, municipio_ibge: null })); setMunicipioSearch(''); }}
                      className="absolute right-2 top-9 text-slate-400 hover:text-red-500 text-lg font-bold">×</button>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Score mínimo p/ alerta</label>
                  <input type="number" min="0" max="100" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={config.min_score} onChange={e => setConfig(c => ({ ...c, min_score: Number(e.target.value) }))} />
                </div>
              </div>

              <div className="mb-5">
                <label className="text-sm font-medium text-slate-700 block mb-2">Período de busca</label>
                <div className="grid grid-cols-3 gap-3 items-end">
                  <div>
                    <label className="text-xs text-slate-500 block mb-1">Data início</label>
                    <input type="date" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={config.data_inicio || ''} onChange={e => setConfig(c => ({ ...c, data_inicio: e.target.value || null }))} />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 block mb-1">Data fim</label>
                    <input type="date" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={config.data_fim || ''} onChange={e => setConfig(c => ({ ...c, data_fim: e.target.value || null }))} />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 block mb-1">Ou: últimos N dias</label>
                    <input type="number" min="1" max="60" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="ex: 7" value={config.days_lookback}
                      onChange={e => setConfig(c => ({ ...c, days_lookback: Number(e.target.value), data_inicio: null, data_fim: null }))} />
                  </div>
                </div>
                {(config.data_inicio || config.data_fim) && (
                  <p className="mt-1 text-xs text-blue-600">
                    Período personalizado ativo — as datas sobrepõem o campo "últimos N dias".
                    <button onClick={() => setConfig(c => ({ ...c, data_inicio: null, data_fim: null }))} className="ml-2 underline text-red-500">Limpar datas</button>
                  </p>
                )}
              </div>

              <div className="mb-5">
                <label className="text-sm font-medium text-slate-700 block mb-2">Valor estimado (R$)</label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-500 block mb-1">Mínimo</label>
                    <input type="number" min="0" step="1000" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="ex: 10000" value={config.valor_min ?? ''}
                      onChange={e => setConfig(c => ({ ...c, valor_min: e.target.value ? Number(e.target.value) : null }))} />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 block mb-1">Máximo</label>
                    <input type="number" min="0" step="1000" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="sem limite" value={config.valor_max ?? ''}
                      onChange={e => setConfig(c => ({ ...c, valor_max: e.target.value ? Number(e.target.value) : null }))} />
                  </div>
                </div>
                <p className="mt-1 text-xs text-slate-400">Licitações sem valor informado sempre aparecem nos resultados.</p>
              </div>

              <div className="mb-5">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-slate-700">Modalidades (PNCP)</label>
                  <div className="flex gap-2">
                    <button onClick={() => setConfig(c => ({ ...c, modalidades: MODALIDADES_PNCP.map(m => m.id) }))} className="text-xs text-blue-600 hover:underline">Todas</button>
                    <span className="text-slate-300">|</span>
                    <button onClick={() => setConfig(c => ({ ...c, modalidades: [] }))} className="text-xs text-red-500 hover:underline">Nenhuma</button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  {MODALIDADES_PNCP.map(m => {
                    const active = (config.modalidades || []).includes(m.id);
                    return (
                      <label key={m.id} className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-xs transition ${active ? 'border-blue-300 bg-blue-50 text-blue-800' : 'border-slate-200 bg-slate-50 text-slate-600'}`}>
                        <input type="checkbox" checked={active} className="h-3.5 w-3.5 accent-blue-600"
                          onChange={e => {
                            const cur = config.modalidades || [];
                            setConfig(c => ({ ...c, modalidades: e.target.checked ? [...cur, m.id] : cur.filter(x => x !== m.id) }));
                          }} />
                        {m.label}
                      </label>
                    );
                  })}
                </div>
                <p className="mt-1 text-xs text-slate-400">Aplica-se apenas ao PNCP. ComprasNet e Diários Oficiais filtram por palavras-chave.</p>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 block mb-2">Palavras-chave</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {config.keywords.map(kw => (
                    <span key={kw} className="flex items-center gap-1 rounded-full bg-blue-100 px-3 py-1 text-sm text-blue-800">
                      {kw}
                      <button onClick={() => removeKeyword(kw)} className="ml-1 text-blue-400 hover:text-red-500 font-bold">×</button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Nova palavra-chave" value={newKw} onChange={e => setNewKw(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addKeyword()} />
                  <button onClick={addKeyword} className="rounded-lg bg-blue-100 px-4 py-2 text-sm font-medium text-blue-700 transition hover:bg-blue-200">
                    + Adicionar
                  </button>
                </div>
              </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <input type="checkbox" id="alert_email" checked={config.alert_email}
                  onChange={e => setConfig(c => ({ ...c, alert_email: e.target.checked }))} className="h-4 w-4 accent-blue-600" />
                <label htmlFor="alert_email" className="text-base font-semibold text-slate-900">Alertas por E-mail</label>
              </div>
              {config.alert_email && (
                <div>
                  <p className="text-xs text-slate-500 mb-3">Adicione todos os e-mails que devem receber alertas de novas licitações.</p>
                  <div className="flex flex-wrap gap-2 mb-3 min-h-[36px]">
                    {(config.alert_email_addresses || []).length === 0 ? (
                      <span className="text-xs text-slate-400 italic">Nenhum e-mail cadastrado</span>
                    ) : (config.alert_email_addresses || []).map(em => (
                      <span key={em} className="flex items-center gap-1 rounded-full bg-green-100 px-3 py-1 text-sm text-green-800">
                        {em}
                        <button onClick={() => removeEmail(em)} className="ml-1 text-green-500 hover:text-red-500 font-bold">×</button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input type="email" className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="email@empresa.com" value={newEmail} onChange={e => setNewEmail(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && addEmail()} />
                    <button onClick={addEmail} className="rounded-lg bg-green-100 px-4 py-2 text-sm font-medium text-green-700 transition hover:bg-green-200 whitespace-nowrap">
                      + Adicionar E-mail
                    </button>
                  </div>
                </div>
              )}
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <input type="checkbox" id="alert_wp" checked={config.alert_whatsapp}
                  onChange={e => setConfig(c => ({ ...c, alert_whatsapp: e.target.checked }))} className="h-4 w-4 accent-green-600" />
                <label htmlFor="alert_wp" className="text-base font-semibold text-slate-900">Alertas por WhatsApp (Synkly)</label>
              </div>
              {config.alert_whatsapp && (
                <div>
                  <p className="text-xs text-slate-500 mb-3">
                    Adicione os números que receberão alertas via WhatsApp. Use o formato internacional com DDI: <strong>+55 11 99999-9999</strong>
                  </p>
                  <div className="flex flex-wrap gap-2 mb-3 min-h-[36px]">
                    {(config.alert_whatsapp_numbers || []).length === 0 ? (
                      <span className="text-xs text-slate-400 italic">Nenhum número cadastrado</span>
                    ) : (config.alert_whatsapp_numbers || []).map(ph => (
                      <span key={ph} className="flex items-center gap-1 rounded-full bg-emerald-100 px-3 py-1 text-sm text-emerald-800">
                        {ph}
                        <button onClick={() => removePhone(ph)} className="ml-1 text-emerald-500 hover:text-red-500 font-bold">×</button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input type="tel" className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="+5511999999999" value={newPhone} onChange={e => setNewPhone(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && addPhone()} />
                    <button onClick={addPhone} className="rounded-lg bg-emerald-100 px-4 py-2 text-sm font-medium text-emerald-700 transition hover:bg-emerald-200 whitespace-nowrap">
                      + Adicionar Número
                    </button>
                  </div>
                  <p className="mt-2 text-xs text-slate-400">
                    Requer Synkly configurado em <strong>Integrações Gerais</strong> com uma conta WhatsApp conectada.
                  </p>
                </div>
              )}
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <input type="checkbox" id="auto_search" checked={config.auto_search_enabled}
                  onChange={e => setConfig(c => ({ ...c, auto_search_enabled: e.target.checked }))} className="h-4 w-4 accent-purple-600" />
                <label htmlFor="auto_search" className="text-base font-semibold text-slate-900">Busca Automática Agendada</label>
              </div>
              {config.auto_search_enabled && (
                <div className="space-y-3">
                  <p className="text-xs text-slate-500">
                    O sistema buscará automaticamente licitações no PNCP no intervalo definido e enviará alertas para os destinatários configurados quando encontrar licitações com score ≥ {config.min_score}.
                  </p>
                  <div className="max-w-xs">
                    <label className="text-sm font-medium text-slate-700 block mb-1">Intervalo de busca (horas)</label>
                    <select className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={config.auto_search_interval_hours}
                      onChange={e => setConfig(c => ({ ...c, auto_search_interval_hours: Number(e.target.value) }))}>
                      <option value={1}>A cada 1 hora</option>
                      <option value={6}>A cada 6 horas</option>
                      <option value={12}>A cada 12 horas</option>
                      <option value={24}>A cada 24 horas (diário)</option>
                      <option value={48}>A cada 48 horas</option>
                      <option value={168}>Semanal</option>
                    </select>
                  </div>
                  <div className="rounded-lg border border-purple-200 bg-purple-50 p-3 text-xs text-purple-700">
                    Quando ativado: o backend rodará a busca automaticamente, sem necessidade de estar no sistema.
                    Alertas serão enviados para <strong>{(config.alert_email_addresses || []).length} e-mail(s)</strong>
                    {(config.alert_whatsapp_numbers || []).length > 0 && <> e <strong>{config.alert_whatsapp_numbers.length} WhatsApp(s)</strong></>}.
                  </div>
                </div>
              )}
            </section>

            <button onClick={handleSaveConfig} disabled={savingConfig}
              className="w-full rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300">
              {savingConfig ? 'Salvando…' : 'Salvar Configurações'}
            </button>
          </div>
        )}

        {selectedLic && (
          <LicDetail lic={selectedLic} onClose={() => setSelectedLic(null)}
            onSave={() => { handleSave(selectedLic); setSelectedLic(null); }}
            onAlert={() => handleAlert(selectedLic)}
            onAnalyzePdf={(url) => { setPdfUrl(url); setTab('edital'); setSelectedLic(null); }}
          />
        )}
      </div>
    </div>
  );
}

function ScoreBadge({ score, size = 'md' }) {
  const sz = size === 'lg' ? 'text-2xl w-16 h-16' : 'text-sm w-12 h-12';
  return (
    <div className={`${sz} rounded-full flex items-center justify-center font-bold flex-shrink-0`}
      style={{ background: `conic-gradient(${SCORE_COLOR(score)} ${score * 3.6}deg, #e5e7eb 0deg)` }}>
      <div className="bg-white rounded-full w-3/4 h-3/4 flex items-center justify-center"
        style={{ color: SCORE_COLOR(score), fontSize: size === 'lg' ? '1rem' : '0.65rem', fontWeight: 700 }}>
        {score}
      </div>
    </div>
  );
}

function LicCard({ lic, onSave, onDelete, onAlert, onDetails, onAnalyze, showDelete, sendingAlert }) {
  const fKey = lic.fonte ? (Object.keys(FONTE_META).find(k => FONTE_META[k].label === lic.fonte) || '') : '';
  const fm = FONTE_META[fKey] || { label: lic.fonte || 'Desconhecido', color: 'bg-gray-100 text-gray-600' };
  return (
    <div className={`rounded-2xl border border-slate-200 bg-white shadow-sm hover:shadow-md transition overflow-hidden ${SCORE_BG(lic.score_aderencia)}`}>
      <div className={`flex items-center justify-between px-4 py-1.5 ${fm.color} border-b border-slate-100`}>
        <span className="text-xs font-semibold">{fm.label}</span>
        <div className="flex gap-1">
          {lic.link_sistema_origem && (
            <a href={lic.link_sistema_origem} target="_blank" rel="noreferrer"
              className="text-xs rounded-md bg-white/60 px-2 py-0.5 transition hover:bg-white/90 font-medium" title="Abrir fonte">
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
          <button onClick={onDetails} className="text-xs rounded-md bg-white/60 px-2 py-0.5 transition hover:bg-white/90" title="Ver detalhes">
            <Eye className="h-3.5 w-3.5" />
          </button>
          {onAnalyze && (
            <button onClick={onAnalyze} className="text-xs rounded-md bg-white/60 px-2 py-0.5 transition hover:bg-white/90" title="Analisar Edital com IA">
              <FileText className="h-3.5 w-3.5" />
            </button>
          )}
          {onSave && (
            <button onClick={onSave} className="text-xs rounded-md bg-white/60 px-2 py-0.5 transition hover:bg-white/90" title="Salvar">
              <Star className="h-3.5 w-3.5" />
            </button>
          )}
          {showDelete && (
            <button onClick={onDelete} className="text-xs rounded-md bg-white/60 px-2 py-0.5 transition hover:bg-red-50" title="Remover">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
          <button onClick={onAlert} disabled={sendingAlert}
            className="text-xs rounded-md bg-white/60 px-2 py-0.5 transition hover:bg-white/90 disabled:opacity-50" title="Enviar alerta">
            {sendingAlert ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Bell className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>
      <div className="flex items-start gap-4 p-4">
        <ScoreBadge score={lic.score_aderencia} />
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-slate-900 text-sm leading-snug line-clamp-2 mb-2">{lic.objeto_compra || '—'}</h3>
          <div className="flex flex-wrap gap-2">
            <span className="text-xs text-slate-500"><Building2 className="inline h-3 w-3 mr-1" />{lic.orgao_nome || '—'}</span>
            <span className="text-xs text-slate-500"><Tag className="inline h-3 w-3 mr-1" />{fmtCurrency(lic.valor_total_estimado)}</span>
            {lic.data_encerramento_proposta && (
              <span className="text-xs text-orange-600"><Clock className="inline h-3 w-3 mr-1" />Encerra: {fmtDate(lic.data_encerramento_proposta)}</span>
            )}
            {lic.modalidade_nome && (
              <span className="text-xs rounded-full bg-purple-100 px-2 py-0.5 text-purple-700">{lic.modalidade_nome}</span>
            )}
            {lic.chance_vitoria && (
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CHANCE_BADGE[lic.chance_vitoria] || 'bg-gray-100 text-gray-700'}`}>
                Chance: {lic.chance_vitoria}
              </span>
            )}
          </div>
          {lic.score_justificativa && (
            <p className={`text-xs mt-2 ${SCORE_TEXT(lic.score_aderencia)}`}>{lic.score_justificativa}</p>
          )}
        </div>
      </div>
    </div>
  );
}

function LicDetail({ lic, onClose, onSave, onAlert, onAnalyzePdf }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 flex items-center justify-between border-b border-slate-200 bg-white px-6 py-4 rounded-t-2xl">
          <div className="flex items-center gap-3">
            <ScoreBadge score={lic.score_aderencia} size="lg" />
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Score de Aderência</p>
              <div className="flex gap-2 mt-1">
                {lic.chance_vitoria && (
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CHANCE_BADGE[lic.chance_vitoria] || 'bg-gray-100'}`}>
                    Chance: {lic.chance_vitoria}
                  </span>
                )}
                {lic.complexidade && (
                  <span className="text-xs rounded-full bg-gray-100 px-2 py-0.5 text-gray-700">
                    Complexidade: {lic.complexidade}
                  </span>
                )}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 transition" aria-label="Fechar">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Objeto</p>
            <p className="text-slate-900">{lic.objeto_compra}</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { label: 'Órgão', value: lic.orgao_nome },
              { label: 'Valor Estimado', value: fmtCurrency(lic.valor_total_estimado) },
              { label: 'Publicação', value: fmtDate(lic.data_publicacao_pncp) },
              { label: 'Encerramento', value: fmtDate(lic.data_encerramento_proposta) },
              { label: 'Modalidade', value: lic.modalidade_nome },
              { label: 'Categoria', value: lic.categoria },
            ].filter(f => f.value && f.value !== '—').map(f => (
              <div key={f.label} className="rounded-lg bg-slate-50 p-3">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">{f.label}</p>
                <p className="text-sm text-slate-800">{f.value}</p>
              </div>
            ))}
          </div>
          {lic.score_justificativa && (
            <div className={`rounded-lg border p-4 ${SCORE_BG(lic.score_aderencia)}`}>
              <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: SCORE_COLOR(lic.score_aderencia) }}>Análise IA</p>
              <p className="text-sm text-slate-700">{lic.score_justificativa}</p>
            </div>
          )}
          {lic.recomendacao && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
              <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-1">Recomendação</p>
              <p className="text-sm text-blue-800">{lic.recomendacao}</p>
            </div>
          )}
          <div className="flex gap-3 pt-2">
            {lic.link_sistema_origem && (
              <a href={lic.link_sistema_origem} target="_blank" rel="noreferrer"
                className="flex-1 text-center rounded-lg bg-blue-600 py-2.5 text-sm font-medium text-white transition hover:bg-blue-700">
                Ver Edital
              </a>
            )}
            {lic.link_sistema_origem && (
              <button onClick={() => onAnalyzePdf(lic.link_sistema_origem)}
                className="flex-1 rounded-lg bg-purple-600 py-2.5 text-sm font-medium text-white transition hover:bg-purple-700">
                Analisar PDF
              </button>
            )}
            <button onClick={onSave}
              className="flex-1 rounded-lg bg-yellow-500 py-2.5 text-sm font-medium text-white transition hover:bg-yellow-600">
              Salvar
            </button>
            <button onClick={onAlert}
              className="flex-1 rounded-lg bg-green-600 py-2.5 text-sm font-medium text-white transition hover:bg-green-700">
              Alertar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
